import { type QueryClient, queryOptions, useMutation } from '@tanstack/react-query'
import type { CreateApiKey, KeyRetirement } from '@headline/schemas'
import { api } from './api'
import { usageWindow } from './api-usage-view'
import { fail } from './query-fail'

/**
 * A workspace's API product (`/api/workspace-api/:subaccountId`): the endpoint Clipbook scoped for
 * it and the keys its people manage. A create or a rotate resolves to the one response that carries
 * a secret; every key write refreshes the workspace's read, which never carries one.
 */
export const workspaceApiQuery = (subaccountId: string) =>
  queryOptions({
    queryKey: ['workspace-api', subaccountId],
    queryFn: async () => {
      const res = await api.api['workspace-api'][':subaccountId'].$get({ param: { subaccountId } })
      if (!res.ok) return fail(res, 'Failed to load the API')
      return res.json()
    },
  })

/**
 * The endpoint's usage per UTC day and key over [from, to) (`ApiUsageSeries`, read from clipbook-api).
 * Null where clipbook-api is not configured (503), so the page says usage is not connected rather than
 * showing zeros. Keyed by day strings: a handful of ranges a day, never a value per keystroke.
 */
export const workspaceApiUsageQuery = (
  subaccountId: string,
  { from, to }: { from: string; to: string },
) =>
  queryOptions({
    queryKey: ['workspace-api', subaccountId, 'usage', from, to],
    queryFn: async () => {
      const res = await api.api['workspace-api'][':subaccountId'].usage.$get({
        param: { subaccountId },
        query: { from, to },
      })
      if (res.status === 503) return null
      if (!res.ok) return fail(res, 'Failed to load usage')
      return res.json()
    },
    staleTime: 60_000,
  })

/**
 * The endpoint's Monthly Uptime Percentage for the current UTC month (CLI-1041), by the contract's rule.
 * Null where clipbook-api is not configured (503), so the page shows no figure rather than 100%.
 */
export const workspaceApiUptimeQuery = (subaccountId: string) =>
  queryOptions({
    queryKey: ['workspace-api', subaccountId, 'uptime'],
    queryFn: async () => {
      const res = await api.api['workspace-api'][':subaccountId'].uptime.$get({
        param: { subaccountId },
      })
      if (res.status === 503) return null
      if (!res.ok) return fail(res, 'Failed to load uptime')
      return res.json()
    },
    staleTime: 5 * 60_000,
  })

/**
 * The API reference (CLI-1050): clipbook-api's OpenAPI document, quickstart and MCP tools as the server
 * read them for this workspace's gate. Null where clipbook-api is not configured (503: a preview), so the
 * page says the reference is not connected. The document changes only when clipbook-api deploys, and the
 * server re-reads its copy a minute after the last read (`REFERENCE_FRESH_MS`), so it is not refetched
 * sooner: an open page follows a deploy within a minute or two.
 */
export const workspaceApiReferenceQuery = (subaccountId: string) =>
  queryOptions({
    queryKey: ['workspace-api', subaccountId, 'reference'],
    queryFn: async () => {
      const res = await api.api['workspace-api'][':subaccountId'].reference.$get({
        param: { subaccountId },
      })
      if (res.status === 503) return null
      if (!res.ok) return fail(res, 'Failed to load the API reference')
      return res.json()
    },
    staleTime: 60_000,
  })

/**
 * Starts the default range's usage read and the month's uptime behind a loader without waiting for them: clipbook-api may be
 * slow or absent, and the page's keys and docs must not wait on it.
 */
export function prefetchWorkspaceUsage(
  queryClient: QueryClient,
  subaccountId: string,
  endpoint: { createdAt: string } | null,
) {
  if (!endpoint) return
  const span = usageWindow('30', new Date(), endpoint.createdAt)
  void queryClient.prefetchQuery(workspaceApiUsageQuery(subaccountId, span))
  void queryClient.prefetchQuery(workspaceApiUptimeQuery(subaccountId))
}

const refresh = (queryClient: QueryClient, subaccountId: string) =>
  Promise.all([
    queryClient.invalidateQueries({ queryKey: ['workspace-api', subaccountId] }),
    // Clipbook's own API page lists the same keys.
    queryClient.invalidateQueries({ queryKey: ['api-endpoints'] }),
  ])

export function useCreateWorkspaceKey(subaccountId: string, queryClient: QueryClient) {
  return useMutation({
    mutationFn: async (input: CreateApiKey) => {
      const res = await api.api['workspace-api'][':subaccountId'].keys.$post({
        param: { subaccountId },
        json: input,
      })
      if (!res.ok) return fail(res, 'Failed to create the key')
      return res.json()
    },
    onSuccess: () => refresh(queryClient, subaccountId),
  })
}

export function useRotateWorkspaceKey(subaccountId: string, queryClient: QueryClient) {
  return useMutation({
    mutationFn: async ({ keyId, retire }: { keyId: string; retire: KeyRetirement }) => {
      const res = await api.api['workspace-api'][':subaccountId'].keys[':keyId'].rotate.$post({
        param: { subaccountId, keyId },
        json: { retire },
      })
      if (!res.ok) return fail(res, 'Failed to rotate the key')
      return res.json()
    },
    onSuccess: () => refresh(queryClient, subaccountId),
  })
}

export function useRevokeWorkspaceKey(subaccountId: string, queryClient: QueryClient) {
  return useMutation({
    mutationFn: async ({ keyId, retire }: { keyId: string; retire: KeyRetirement }) => {
      const res = await api.api['workspace-api'][':subaccountId'].keys[':keyId'].revoke.$post({
        param: { subaccountId, keyId },
        json: { retire },
      })
      if (!res.ok) return fail(res, 'Failed to revoke the key')
      return res.json()
    },
    onSuccess: () => refresh(queryClient, subaccountId),
  })
}
